import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, effect, inject, input, output, signal } from '@angular/core';
import { EAbaPreMedico } from '@/features/servicos/shared/enums/abas-servicos.enum';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MenuItem, MessageService } from 'primeng/api';
import { CardContent } from '@/shared/components/card-content/card-content';
import { DialogModule } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { InputMask } from 'primeng/inputmask';
import { Select } from 'primeng/select';
import { Button } from 'primeng/button';
import { PaisService } from '@/core/api/sicom-api/registro/medico/pais/pais.service';
import { MunicipioService } from '@/core/api/sicom-api/registro/medico/municipio/municipio.service';
import { PesquisarEscolaDialogComponent, type PesquisaEscolaResult } from '@/features/registro/medicos/dashboard-medicos/components/formacao/dialogs/pesquisar-escola/pesquisar-escola';
import type { PreMedicoDTO, PreMedicoResponse } from '@/core/api/sicom-api/servicos/pessoa-fisica/pre-medico/pre-medico.model';
import { PreMedicoServicosService } from '@/features/servicos/pessoa-fisica/content/pre-medico-servicos/pre-medico-servicos.service';
import { VisualizadorPdf } from '@/shared/components/visualizador-pdf/visualizador-pdf';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { iconeDaSecao, secaoEhDeDados, subtituloDaSecao } from '@/features/servicos/shared/utils/secao-detalhamento.utils';
import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
import {
    formatarDataMascara,
    montarPatchFormulario,
    montarRequestProtocolo,
    paraNumero,
    simNaoBoolean,
    valorTexto,
    type MapaAliasesFormulario,
    type MapaTransformadoresFormulario,
    type ProtocoloResumoConsulta
} from '@/features/servicos/shared/utils/protocolo-consulta.utils';

// Componente de formulário "Pré Médico" para Serviços (PF)
// Segue o padrão utilizado em "formacao" (standalone + ReactiveForms + PrimeNG)
// As abas sao controladas externamente pelo dashboard de servicos.

interface AnexoPreMedico {
    chave: string;
    titulo: string;
    linkAnexo: string;
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-pre-medico-servicos',
    standalone: true,
    imports: [DialogoPadraoDirective, ReactiveFormsModule, CardContent, DialogModule, VisualizadorPdf, InputText, InputMask, Select, Button, CanDirective, PesquisarEscolaDialogComponent],
    templateUrl: './pre-medico-servicos.html',
    styleUrl: './pre-medico-servicos.scss'
})
export class PreMedicoServicos implements OnInit {
    /**
     * Apresentação das seções — ícone, subtítulo e se a seção é de dados — vem do vocabulário
     * compartilhado: o mesmo título tem a mesma cara em todos os detalhamentos.
     */
    protected readonly iconeDaSecao = iconeDaSecao;
    protected readonly subtituloDaSecao = subtituloDaSecao;
    protected readonly secaoEhDeDados = secaoEhDeDados;

    protected readonly EAbaPreMedico = EAbaPreMedico;
    readonly protocoloSelecionado = input<ProtocoloResumoConsulta | null>(null);
    readonly codigoEstado = input('');
    readonly abaSelecionadaExterna = input<EAbaPreMedico | null>(null);
    /**
     * A efetivação deste protocolo já ocorreu NESTA tela — decidido pelo card-pai (`CardServicos`),
     * que é quem recebe o CRM de volta da confirmação. Clicar "Efetivar solicitação" de novo
     * repetiria a chamada ao portal e geraria um SEGUNDO CRM para o mesmo protocolo, órfão do
     * primeiro. Mesmo risco que o quadro fecha com `conclusoesAceitasNoPortal` (ver
     * `kanban-protocolos.ts`); aqui o gatilho é mais simples porque já existe: ter CRM.
     */
    readonly efetivacaoJaConcluida = input(false);
    readonly finalizarWizard = output<void>();

    protected readonly rolePreMedicoEditar = PERMISSOES.pessoaFisica.preMedico.editar;
    protected readonly rolePreMedicoConfirmar = PERMISSOES.pessoaFisica.preMedico.confirmar;

    private readonly destroyRef = inject(DestroyRef);
    private ultimaChaveConsulta = '';
    private dadosOriginais: PreMedicoDTO | null = null;

    readonly abaSelecionada = signal<EAbaPreMedico>(EAbaPreMedico.DADOS);
    readonly erroEtapa = signal<string | null>(null);
    readonly editando = signal(false);

    /**
     * A aba aberta aceita edição? Só "Anexos" não aceita: ela lista arquivos enviados, e não há campo
     * para alterar ali.
     */
    readonly abaEditavel = computed(() => this.abaSelecionada() !== EAbaPreMedico.ANEXOS);

    /**
     * Ações do formulário, agrupadas num menu só — o mesmo arranjo do "Dados de Registro" em médicos.
     *
     * Na aba sem edição o item fica DESABILITADO em vez de sumir: botão que desaparece ao trocar de
     * aba se lê como defeito, enquanto o item apagado diz que a ação existe e só não vale ali.
     *
     * `computed`, e não um array fixo: `disabled` é uma propriedade do item, então a lista precisa ser
     * remontada quando a aba muda.
     */
    readonly acoesFormulario = computed<MenuItem[]>(() => [{ label: 'Editar', icon: 'pi pi-pencil', disabled: !this.abaEditavel(), command: () => this.habilitarEdicao() }]);
    readonly jsonAtualizacao = signal('');
    readonly salvando = signal(false);
    readonly mensagemAtualizacao = signal<string | null>(null);
    readonly anexosDisponiveis = signal<AnexoPreMedico[]>([]);
    readonly exibirDialogAnexo = signal(false);
    readonly blobAnexo = signal<Blob | null>(null);
    readonly nomeArquivoAnexo = signal('anexo-pre-medico.pdf');
    readonly exibirPesquisaEscola = signal(false);

    private readonly aliasesFormulario: MapaAliasesFormulario = {
        categoria: ['categoria', 'IN_CATEGORIA'],
        periodoDe: 'DT_PERIODO_I',
        periodoAte: 'DT_PERIODO_F',
        nomePai: ['nome_pai', 'NM_PAI'],
        nomeMae: ['nome_mae', 'NM_MAE'],
        dtNasc: ['data_nascimento', 'DT_NASCIMENTO'],
        nacionalidade: ['nacionalidade', 'in_estrangeiro', 'IN_ESTRANGEIRO'],
        pais: ['pais_origem_id', 'PAIS_ID_PAIS', 'ID_PAIS_NACION'],
        naturalidadeCidade: ['municipio_naturalidade', 'NM_MUNICIPIO_EXTERIOR', 'DS_MUN_NATURAL', 'DS_MUNICIPIO'],
        // Sem `er_uf`/`uf` na lista: são a UF do endereço residencial e a do CRM. Como nenhum alias
        // em MAIÚSCULAS casa (o DTO só declara `naturalidade_uf`), a busca caía neles e a tela exibia
        // uma UF plausível e errada — que o salvar então regravava em `naturalidade_uf`.
        naturalidadeUf: ['naturalidade_uf', 'UF_MUN_NATURAL', 'uf_mun_natural'],
        sexo: ['sexo', 'IN_SEXO'],
        estadoCivil: ['estado_civil', 'IN_ESTADO_CIVIL'],
        tipoSanguineoABO: ['grupo_sanguineo', 'IN_TIPO_SANGUE'],
        tipoSanguineoRh: ['fator_rh', 'IN_FATOR_RH'],
        localTrabalho: 'TX_HISTORICO_MEDICO',
        visto: 'IN_ESTR_PROV_PERM',
        numeroRg: ['rg_numero', 'NU_RG'],
        orgExpedidor: ['rg_orgao_expedidor', 'NM_ORGAO_EXPEDIDOR'],
        ufRg: ['rg_uf', 'SG_UF_RG_EXPEDIDA'],
        dtEmissaoRg: ['rg_data_emissao', 'DT_EXPEDICAO_RG'],
        cpfNumero: ['cpf', 'NU_CPF_CGC'],
        tituloNumero: ['te_numero', 'NU_TITULO_ELEITOR'],
        tituloZona: ['te_zona', 'DS_ZONA'],
        tituloSecao: ['te_secao', 'DS_SECAO'],
        tituloUf: ['te_uf', 'SG_UF_TITULO_ELEITOR'],
        numeroReservista: ['reservista_numero', 'NU_CERTIFICADO_MILITAR'],
        situacaoMilitar: ['situacao_militar', 'IN_SITUACAO_MILITAR'],
        regiaoMilitar: ['reservista_regiao_militar', 'NM_REGIAO'],
        // `dataVencimento` é o vencimento do BOLETO (bloco de pagamento do DTO), não a validade do
        // certificado militar. Enquanto o portal não devolver o campo, é melhor vazio que errado.
        validadeCertMilitar: 'DT_VAL_CERTIFICADO_MILITAR',
        comandoMilitar: ['comando', 'IN_MINISTERIO', 'DS_CATEGORIA_MILITAR', 'NM_MINISTERIO'],
        cedulaEstrangeira: ['numeroCedulaEstrangeira', 'NU_CEDULA_ESTRANGEIRA'],
        dtEmissaoCedula: ['dataCedulaEstrangeira', 'DT_CEDULA_ESTRANGEIRA'],
        numeroPassaporte: 'NU_PASSAPORTE',
        dtValidadeVisto: ['data_validade_visto', 'DT_VAL_CEDULA_ESTRANGEIRA'],
        cep: ['er_cep', 'NU_CEP', 'NU_CEP_R'],
        tipoEndereco: 'IN_TIPO_ENDERECO',
        endereco: ['er_logradouro', 'TX_ENDERECO', 'TX_ENDERECO_R'],
        bairro: ['er_bairro', 'NM_BAIRRO', 'NM_BAIRRO_R'],
        municipio: ['ds_mun_end_r', 'ds_mun_end_c', 'DS_MUNICIPIO', 'DS_MUN_END_R', 'DS_MUN_END_C'],
        municipioId: ['er_municipio_id', 'ID_MUNICIPIO_ENDERECO'],
        cepCorrespondencia: 'ec_cep',
        enderecoCorrespondenciaTexto: 'ec_logradouro',
        bairroCorrespondencia: 'ec_bairro',
        municipioCorrespondencia: 'ds_mun_end_c',
        municipioCorrespondenciaId: 'ec_municipio_id',
        telefoneResidencialDdd: 'er_ddd',
        telefoneResidencialNumero: 'er_telefone',
        telefoneCorrespondenciaDdd: 'ec_ddd',
        telefoneCorrespondenciaNumero: 'ec_telefone',
        enderecoCorrespondencia: ['in_endereco_correspondencia', 'IN_ENDERECO_CORRESPONDENCIA', 'IN_END_CORRESPONDENCIA'],
        enderecoAtualizado: 'IN_ENDERECO_ATUAL',
        divulgarEndereco: ['IN_DIVULGA', 'er_autoriza', 'ec_autoriza', 'IN_DIVULGA_R', 'IN_DIVULGA_C'],
        receberJornal: 'QT_JORNAL',
        receberInformativos: 'IN_SITUACAO',
        email: ['nm_email', 'NM_EMAIL'],
        emailTipo: 'IN_TIPO_EMAIL',
        telefoneDdd: ['celular_ddd', 'NU_CODIGO_DDD', 'er_ddd', 'ec_ddd'],
        telefoneNumero: ['celular_numero', 'NU_TELEFONE', 'er_telefone', 'ec_telefone'],
        horarioContato: 'TX_HORARIO_CONTATO',
        telefoneTipo: 'IN_TIPO_TELEFONE',
        deficienciaTipo: ['tipoDeficiencia', 'IN_TIPO_DEFICIENCIA'],
        deficienciaOutras: 'TX_OUTRA_DEFICIENCIA',
        deficienciaOrigem: ['origemDeficiencia', 'IN_ORIGEM_DEFICIENCIA'],
        deficienciaAdaptado: ['adaptadoDeficiencia', 'IN_ADAPTADO_DEFICIENCIA'],
        tipoFormacao: 'TIPO_FORMA_ID_TIPO_FORMACAO',
        comprovanteGraduacao: 'IN_TIPO_DIPLOMA',
        nomeEscola: ['graduacao', 'NM_ESCOLA'],
        paisFormacao: ['ID_PAIS', 'ID_PAIS_ESCOLA'],
        nomeEscolaEstrangeira: ['nomeFaculdadeEstrangeira', 'NM_FACULDADE_ESTRANGEIRA'],
        livroRegistro: ['registro_mec_livro', 'NU_LIVRO_DIPLOMA'],
        numeroProcesso: 'NU_PROCESSO_DIPLOMA',
        inicioCurso: 'DT_INICIO_CURSO',
        dtConclusao: ['data_conclusao', 'DT_CONCLUSAO_CURSO'],
        folhaNumero: ['registro_mec_folha', 'NU_FOLHA_DIPLOMA'],
        registroMec: ['registro_mec_numero', 'NU_REGISTRO_MEC'],
        dtFormaturaForm: ['data_formatura', 'DT_FORMATURA'],
        expDiploma: ['data_expedicao_diploma', 'DT_EXPEDICAO_DIPLOMA'],
        // `dataEmissao` é a emissão do BOLETO. Como `DT_REGISTRO_MEC` nunca casa, a tela exibia essa
        // data e o salvar a regravava em `data_registro_mec` — o campo certo, que estava logo ali.
        dtRegMec: ['data_registro_mec', 'DT_REGISTRO_MEC'],
        apresentouDiploma: 'IN_APRESENTOU_DIPLOMA'
    };
    readonly etapasWizard: ReadonlyArray<{ aba: EAbaPreMedico; rotulo: string }> = [
        { aba: EAbaPreMedico.DADOS, rotulo: 'Dados pessoais' },
        { aba: EAbaPreMedico.DOC, rotulo: 'Documentos' },
        { aba: EAbaPreMedico.CORR, rotulo: 'Correspondencia' },
        { aba: EAbaPreMedico.TEL, rotulo: 'Telefone' },
        { aba: EAbaPreMedico.FORM, rotulo: 'Formacao' },
        { aba: EAbaPreMedico.DEF, rotulo: 'Deficiencias' },
        { aba: EAbaPreMedico.ANEXOS, rotulo: 'Anexos' },
        { aba: EAbaPreMedico.REVISAO, rotulo: 'Revisao' }
    ];

    private readonly transformadoresFormulario: MapaTransformadoresFormulario = {
        periodoDe: formatarDataMascara,
        periodoAte: formatarDataMascara,
        dtNasc: formatarDataMascara,
        dtEmissaoRg: formatarDataMascara,
        validadeCertMilitar: formatarDataMascara,
        dtEmissaoCedula: formatarDataMascara,
        dtValidadeVisto: formatarDataMascara,
        inicioCurso: formatarDataMascara,
        dtConclusao: formatarDataMascara,
        dtFormaturaForm: formatarDataMascara,
        expDiploma: formatarDataMascara,
        dtRegMec: formatarDataMascara,
        enderecoCorrespondencia: simNaoBoolean,
        enderecoAtualizado: simNaoBoolean,
        divulgarEndereco: simNaoBoolean,
        receberJornal: simNaoBoolean,
        receberInformativos: simNaoBoolean,
        deficienciaAdaptado: simNaoBoolean,
        apresentouDiploma: simNaoBoolean,
        // A API devolve estes ids como texto; as listas de `p-select` usam `value: number`.
        pais: paraNumero,
        paisFormacao: paraNumero,
        municipioId: paraNumero,
        municipioCorrespondenciaId: paraNumero
    };

    /** Controles ligados a listas carregadas por HTTP — ver `reaplicarSelecoesDependentesDeLista`. */
    private static readonly CONTROLES_DEPENDENTES_DE_LISTA = ['pais', 'paisFormacao', 'municipioId', 'municipioCorrespondenciaId'] as const;

    private readonly camposAnexos: ReadonlyArray<{ chave: keyof PreMedicoDTO; titulo: string }> = [
        { chave: 'anexoCpf', titulo: 'CPF' },
        { chave: 'anexoRg', titulo: 'RG' },
        { chave: 'anexoTitulo', titulo: 'Titulo de Eleitor' },
        { chave: 'anexoDocumentoMilitar', titulo: 'Documento Militar' },
        { chave: 'anexoDiploma', titulo: 'Diploma' },
        { chave: 'anexoCertidaoColacao', titulo: 'Certidao de Colacao' },
        { chave: 'anexoComprovanteDeclaracaoResidencia', titulo: 'Comprovante/Declaracao de Residencia' },
        { chave: 'anexoCertificadoRevalidacao', titulo: 'Certificado de Revalidacao' },
        { chave: 'anexoInscricaoJudicial', titulo: 'Inscricao Judicial' },
        { chave: 'anexoTraducaoJuramentada', titulo: 'Traducao Juramentada' },
        { chave: 'anexoCertificadoDeProficiencia', titulo: 'Certificado de Proficiencia' },
        { chave: 'anexoDocumentoIdentidade', titulo: 'Documento de Identidade' }
    ];

    categorias = [
        { label: 'Civil', value: 'C' },
        { label: 'Militar', value: 'M' },
        { label: 'Civil Convocado', value: 'V' }
    ];

    nacionalidades = [
        { label: 'Brasileiro', value: 'B' },
        { label: 'Estrangeiro', value: 'E' },
        { label: 'Naturalizado', value: 'N' }
    ];

    paises: Array<{ label: string; value: number }> = [];
    municipios: Array<{ label: string; value: number; cep: string }> = [];

    estadosCivis = [
        { label: 'Casado', value: 'C' },
        { label: 'Solteiro', value: 'S' },
        { label: 'Divorciado', value: 'D' },
        { label: 'Viúvo', value: 'V' },
        { label: 'Separado', value: 'P' },
        { label: 'Outros', value: 'O' }
    ];

    sexos = [
        { label: 'Masculino', value: 'M' },
        { label: 'Feminino', value: 'F' }
    ];

    tiposSanguineosABO = [
        { label: 'A', value: 'A' },
        { label: 'B', value: 'B' },
        { label: 'AB', value: 'AB' },
        { label: 'O', value: 'O' }
    ];

    tiposSanguineosRh = [
        { label: '+', value: '+' },
        { label: '-', value: '-' }
    ];

    vistos = [
        { label: 'Permanente', value: 'P' },
        { label: 'Provisório', value: 'V' }
    ];

    // Formação options
    tipoFormacaoOptions = [
        { label: 'Graduação', value: 6 },
        { label: 'Revalidação', value: 7 },
        { label: 'Pré-Médico', value: 9 }
    ];

    comprovanteGraduacaoOptions = [
        { label: 'Diploma', value: 'D' },
        { label: 'E-Diploma', value: 'E' },
        { label: 'Declaração / Certidão de Colação de Grau', value: 'C' },
        { label: 'Liminar', value: 'L' }
    ];

    // Opções genéricas Sim/Não para selects (substitui radio)
    yesNoOptions = [
        { label: 'Sim', value: true },
        { label: 'Não', value: false }
    ];

    // Tipo de endereço para Correspondência
    tipoEnderecoOptions = [
        { label: 'Residencial', value: 'R' },
        { label: 'Comercial', value: 'C' },
        { label: 'Personalidade', value: 'P' }
    ];

    // Tipo de e-mail
    emailTipoOptions = [
        { label: 'Comercial', value: 'C' },
        { label: 'Pessoal', value: 'P' },
        { label: 'Federal', value: 'F' }
    ];

    // Tipo de Telefone
    telefoneTipoOptions = [
        { label: 'Fixo', value: 'F' },
        { label: 'Celular', value: 'C' },
        { label: 'Fax', value: 'X' }
    ];

    // Deficiências
    deficienciaTipoOptions = [
        { label: 'Visual', value: 'V' },
        { label: 'Motora', value: 'M' },
        { label: 'Auditiva', value: 'A' },
        { label: 'Outras', value: 'O' }
    ];

    deficienciaOrigemOptions = [
        { label: 'Congênita', value: 'C' },
        { label: 'Adquirida', value: 'A' }
    ];

    // UF options reused in multiple sections
    ufs = [
        { label: 'AC', value: 'AC' },
        { label: 'AL', value: 'AL' },
        { label: 'AP', value: 'AP' },
        { label: 'AM', value: 'AM' },
        { label: 'BA', value: 'BA' },
        { label: 'CE', value: 'CE' },
        { label: 'DF', value: 'DF' },
        { label: 'ES', value: 'ES' },
        { label: 'GO', value: 'GO' },
        { label: 'MA', value: 'MA' },
        { label: 'MT', value: 'MT' },
        { label: 'MS', value: 'MS' },
        { label: 'MG', value: 'MG' },
        { label: 'PA', value: 'PA' },
        { label: 'PB', value: 'PB' },
        { label: 'PR', value: 'PR' },
        { label: 'PE', value: 'PE' },
        { label: 'PI', value: 'PI' },
        { label: 'RJ', value: 'RJ' },
        { label: 'RN', value: 'RN' },
        { label: 'RS', value: 'RS' },
        { label: 'RO', value: 'RO' },
        { label: 'RR', value: 'RR' },
        { label: 'SC', value: 'SC' },
        { label: 'SP', value: 'SP' },
        { label: 'SE', value: 'SE' },
        { label: 'TO', value: 'TO' }
    ];

    situacaoMilitarOptions = [
        { label: 'Quite', value: 'Q' },
        { label: 'Dispensado', value: 'D' },
        { label: 'Ativo', value: 'A' },
        { label: 'Reserva', value: 'R' },
        { label: 'Outros', value: 'O' }
    ];

    comandoOptions = [
        { label: 'Marinha', value: 'M' },
        { label: 'Exército', value: 'E' },
        { label: 'Aeronáutica', value: 'A' }
    ];

    private readonly fb = inject(FormBuilder);
    readonly form = this.fb.nonNullable.group({
        // Aba DADOS
        categoria: [''],
        periodoDe: [''],
        periodoAte: [''],
        nomePai: [''],
        nomeMae: [''],
        dtNasc: [''],
        nacionalidade: [''],
        pais: this.fb.control<number | null>(null),
        naturalidadeCidade: [''],
        naturalidadeUf: [''],
        sexo: [''], // era radio; agora p-select
        estadoCivil: [''],
        tipoSanguineoABO: [''],
        tipoSanguineoRh: [''],
        localTrabalho: [''],
        visto: [''], // usa p-select

        // Aba DOCUMENTOS
        numeroRg: [''],
        orgExpedidor: [''],
        ufRg: [''],
        dtEmissaoRg: [''],

        cpfNumero: [''],

        tituloNumero: [''],
        tituloZona: [''],
        tituloSecao: [''],
        tituloUf: [''],

        numeroReservista: [''],
        situacaoMilitar: [''],
        regiaoMilitar: [''],
        validadeCertMilitar: [''],
        comandoMilitar: [''],

        cedulaEstrangeira: [''],
        dtEmissaoCedula: [''],
        numeroPassaporte: [''],
        dtValidadeVisto: [''],

        // Aba CORRESPONDÊNCIA
        cep: [''],
        tipoEndereco: [''],
        endereco: [''],
        bairro: [''],
        municipio: [''],
        municipioId: this.fb.control<number | null>(null),
        cepCorrespondencia: [''],
        enderecoCorrespondenciaTexto: [''],
        bairroCorrespondencia: [''],
        municipioCorrespondencia: [''],
        municipioCorrespondenciaId: this.fb.control<number | null>(null),
        enderecoCorrespondencia: [true],
        enderecoAtualizado: [true],
        divulgarEndereco: [true],
        receberJornal: [true],
        receberInformativos: [true],
        email: [''],
        emailTipo: [''],

        // Aba TELEFONE
        telefoneDdd: [''],
        telefoneNumero: [''],
        telefoneResidencialDdd: [''],
        telefoneResidencialNumero: [''],
        telefoneCorrespondenciaDdd: [''],
        telefoneCorrespondenciaNumero: [''],
        horarioContato: [''],
        telefoneTipo: [''],

        // Aba DEFICIÊNCIAS
        deficienciaTipo: [''],
        deficienciaOutras: [''],
        deficienciaOrigem: [''],
        deficienciaAdaptado: this.fb.control<boolean | null>(null),

        // Aba FORMAÇÃO
        tipoFormacao: [''],
        comprovanteGraduacao: [''],
        nomeEscola: [''],
        paisFormacao: this.fb.control<string | number | null>(''),
        nomeEscolaEstrangeira: [''],
        livroRegistro: [''],
        numeroProcesso: [''],
        inicioCurso: [''],
        dtConclusao: [''],
        folhaNumero: [''],
        registroMec: [''],
        dtFormaturaForm: [''],
        expDiploma: [''],
        dtRegMec: [''],
        apresentouDiploma: this.fb.control<boolean | null>(null)
    });
    private readonly servico = inject(PreMedicoServicosService);
    private readonly paisService = inject(PaisService);
    private readonly municipioService = inject(MunicipioService);
    private readonly messageService = inject(MessageService);

    constructor() {
        // Sincroniza a aba com o dashboard (input) e recarrega ao mudar protocolo/UF (o form já existe: efeitos correm após ngOnInit).
        effect(() => {
            this.abaSelecionadaExterna();
            this.sincronizarAbaExterna();
        });
        effect(() => {
            this.protocoloSelecionado();
            this.codigoEstado();
            this.carregarDados();
        });
    }

    ngOnInit(): void {
        this.carregarPaises();
        this.carregarMunicipios();

        this.form.disable({ emitEvent: false });
    }

    abrirPesquisaEscola(): void {
        this.exibirPesquisaEscola.set(true);
    }

    selecionarEscola(escola: PesquisaEscolaResult): void {
        this.form.patchValue({ nomeEscola: escola.nome, paisFormacao: escola.uf === 'BR' ? (this.paises.find((pais) => pais.label === 'Brasil')?.value ?? null) : null });
    }

    private carregarPaises(): void {
        this.paisService
            .listarPaises()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (paises) => {
                    this.paises = (paises ?? []).map((pais) => ({ label: pais.nmPais, value: pais.id }));
                    this.reaplicarSelecoesDependentesDeLista();
                },
                error: () => (this.paises = [])
            });
    }

    preencherMunicipioPorCep(): void {
        const cep = valorTexto(this.form.get('cep')?.value).replace(/\D/g, '');
        const municipio = this.municipios.find((item) => item.cep.replace(/\D/g, '') === cep);
        if (municipio) {
            this.form.patchValue({ municipio: municipio.label, municipioId: municipio.value });
        }
    }

    onMunicipioSelecionado(id: number | null): void {
        const municipio = this.municipios.find((item) => item.value === id);
        if (municipio) {
            this.form.patchValue({ municipio: municipio.label, municipioId: municipio.value });
        }
    }

    onMunicipioCorrespondenciaSelecionado(id: number | null): void {
        const municipio = this.municipios.find((item) => item.value === id);
        if (municipio) {
            this.form.patchValue({ municipioCorrespondencia: municipio.label, municipioCorrespondenciaId: municipio.value });
        }
    }

    private carregarMunicipios(): void {
        this.municipioService
            .listarMunicipios()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (municipios) => {
                    this.municipios = (municipios ?? []).map((municipio) => ({ label: municipio.dsMunicipio, value: municipio.id, cep: municipio.nuCep ?? '' }));
                    this.reaplicarSelecoesDependentesDeLista();
                },
                error: () => (this.municipios = [])
            });
    }

    /**
     * O protocolo quase sempre chega antes de `listarPaises`/`listarMunicipios`. Quando isso
     * acontece o `p-select` já tentou casar o valor contra uma lista vazia e fica no placeholder,
     * mesmo com o id correto no controle. Reaplicar `null` → valor força o casamento de novo, agora
     * que as opções existem. `emitEvent: false` para não marcar o formulário como sujo.
     */
    private reaplicarSelecoesDependentesDeLista(): void {
        for (const nome of PreMedicoServicos.CONTROLES_DEPENDENTES_DE_LISTA) {
            const controle = this.form.get(nome);
            const valor = controle?.value;

            if (!controle || valor === null || valor === undefined || valor === '') {
                continue;
            }

            controle.setValue(null, { emitEvent: false });
            controle.setValue(valor, { emitEvent: false });
        }
    }

    private sincronizarAbaExterna(): void {
        const aba = this.abaSelecionadaExterna();
        if (!aba) {
            return;
        }

        this.abaSelecionada.set(aba);

        if (this.editando() && aba === EAbaPreMedico.ANEXOS) {
            this.cancelarEdicao();
        }
    }

    indiceEtapaAtual(): number {
        return Math.max(
            0,
            this.etapasWizard.findIndex((etapa) => etapa.aba === this.abaSelecionada())
        );
    }

    progressoWizard(): number {
        return ((this.indiceEtapaAtual() + 1) / this.etapasWizard.length) * 100;
    }

    etapaAnterior(): void {
        const indice = this.indiceEtapaAtual();
        if (indice > 0) {
            this.erroEtapa.set(null);
            this.abaSelecionada.set(this.etapasWizard[indice - 1].aba);
        }
    }

    proximaEtapa(): void {
        const erro = this.validarEtapaAtual();
        if (erro) {
            this.erroEtapa.set(erro);
            this.messageService.add({ severity: 'warn', summary: 'Validacao', detail: erro });
            return;
        }
        const indice = this.indiceEtapaAtual();
        if (indice < this.etapasWizard.length - 1) {
            this.erroEtapa.set(null);
            this.abaSelecionada.set(this.etapasWizard[indice + 1].aba);
        }
    }

    concluirWizard(): void {
        // `disabled` no botão é aparência: chamada direta (atalho, teste, código futuro) tem de
        // respeitar a mesma regra, senão uma segunda efetivação sobre o mesmo protocolo geraria um
        // segundo CRM, órfão do primeiro.
        if (this.efetivacaoJaConcluida()) {
            return;
        }
        const erro = this.validarFormulario();
        if (erro) {
            this.erroEtapa.set(erro);
            this.messageService.add({ severity: 'warn', summary: 'Validacao', detail: erro });
            return;
        }
        if (this.editando()) {
            const mensagem = 'Salve as alteracoes antes de efetivar a solicitacao.';
            this.erroEtapa.set(mensagem);
            this.messageService.add({ severity: 'warn', summary: 'Validacao', detail: mensagem });
            return;
        }
        this.finalizarWizard.emit();
    }

    private validarEtapaAtual(): string | null {
        const valores = this.form.getRawValue();
        switch (this.abaSelecionada()) {
            case EAbaPreMedico.DADOS:
                return valorTexto(valores.nacionalidade).trim() ? null : 'Informe a nacionalidade.';
            case EAbaPreMedico.DOC:
                if (this.isEstrangeiro) {
                    return valorTexto(valores.cedulaEstrangeira).trim() ? null : 'Informe o documento de identidade estrangeira.';
                }
                return valorTexto(valores.cpfNumero).trim() && valorTexto(valores.numeroRg).trim() ? null : 'Informe CPF e RG.';
            case EAbaPreMedico.CORR:
                return valorTexto(valores.cep).trim() && valorTexto(valores.endereco).trim() ? null : 'Informe CEP e endereco de correspondencia.';
            case EAbaPreMedico.TEL:
                return valorTexto(valores.telefoneDdd).trim() && valorTexto(valores.telefoneNumero).trim() ? null : 'Informe DDD e telefone.';
            case EAbaPreMedico.FORM:
                return this.validarFormulario();
            default:
                return null;
        }
    }

    private carregarDados(): void {
        const request = montarRequestProtocolo(this.protocoloSelecionado(), this.codigoEstado());

        if (!request) {
            this.ultimaChaveConsulta = '';
            this.anexosDisponiveis.set([]);
            this.form.reset({
                enderecoCorrespondencia: true,
                enderecoAtualizado: true,
                divulgarEndereco: true,
                receberJornal: true,
                receberInformativos: true,
                deficienciaAdaptado: null,
                apresentouDiploma: null
            });
            this.form.disable({ emitEvent: false });
            this.editando.set(false);
            this.jsonAtualizacao.set('');
            this.dadosOriginais = null;
            return;
        }

        const chaveConsulta = `${request.numeroProtocolo}/${request.anoProtocolo}-${request.ufCrm}`;
        if (this.ultimaChaveConsulta === chaveConsulta) {
            return;
        }

        this.ultimaChaveConsulta = chaveConsulta;

        this.servico
            .consultar(request)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (response) => {
                    this.dadosOriginais = response;
                    const patch = montarPatchFormulario(this.form, response as Record<string, unknown>, this.aliasesFormulario, this.transformadoresFormulario);
                    this.atualizarAnexos(response);

                    this.form.patchValue(patch, { emitEvent: false });
                    this.form.disable({ emitEvent: false });
                    this.editando.set(false);
                    this.jsonAtualizacao.set('');
                },
                error: () => {
                    this.anexosDisponiveis.set([]);
                    this.form.reset({
                        enderecoCorrespondencia: true,
                        enderecoAtualizado: true,
                        divulgarEndereco: true,
                        receberJornal: true,
                        receberInformativos: true,
                        deficienciaAdaptado: null,
                        apresentouDiploma: null
                    });
                    this.form.disable({ emitEvent: false });
                    this.editando.set(false);
                    this.jsonAtualizacao.set('');
                    this.dadosOriginais = null;
                }
            });
    }

    habilitarEdicao(): void {
        // Guarda a mesma regra que apaga o item no menu: `disabled` é aparência, e chamada por outro
        // caminho (atalho, teste, código futuro) abriria a edição numa aba que não tem o que salvar.
        if (!this.abaEditavel()) {
            return;
        }

        this.editando.set(true);
        this.jsonAtualizacao.set('');
        this.mensagemAtualizacao.set(null);
        this.form.enable({ emitEvent: false });
    }

    cancelarEdicao(): void {
        this.editando.set(false);
        this.jsonAtualizacao.set('');
        this.mensagemAtualizacao.set(null);
        this.form.disable({ emitEvent: false });
        this.ultimaChaveConsulta = '';
        this.carregarDados();
    }

    salvarEdicao(): void {
        if (this.salvando()) {
            return;
        }

        const erroValidacao = this.validarFormulario();
        if (erroValidacao) {
            this.mensagemAtualizacao.set(erroValidacao);
            this.messageService.add({ severity: 'warn', summary: 'Validacao', detail: erroValidacao });
            return;
        }

        const objetoAtualizacao = this.montarObjetoAtualizacao();
        this.jsonAtualizacao.set(JSON.stringify(objetoAtualizacao, null, 2));
        this.salvando.set(true);
        this.mensagemAtualizacao.set(null);

        this.servico
            .atualizarCadastro(objetoAtualizacao)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.salvando.set(false);
                    this.editando.set(false);
                    this.mensagemAtualizacao.set('Cadastro atualizado com sucesso.');
                    this.dadosOriginais = objetoAtualizacao;
                    this.form.disable({ emitEvent: false });
                    this.ultimaChaveConsulta = '';
                    this.carregarDados();
                },
                error: () => {
                    this.salvando.set(false);
                    this.mensagemAtualizacao.set('Falha ao atualizar cadastro do pré-médico.');
                    // Mantem o JSON exibido para facilitar diagnostico.
                }
            });
    }

    private montarObjetoAtualizacao(): PreMedicoDTO {
        const valores = this.form.getRawValue();
        const protocolo = montarRequestProtocolo(this.protocoloSelecionado(), this.codigoEstado());
        const uf = (protocolo?.ufCrm ?? '').trim().toUpperCase();

        return {
            ...(this.dadosOriginais ?? {}),
            numeroProtocolo: protocolo?.numeroProtocolo ?? this.dadosOriginais?.numeroProtocolo,
            anoProtocolo: protocolo?.anoProtocolo ?? this.dadosOriginais?.anoProtocolo,
            uf: uf || this.dadosOriginais?.uf,
            cpf: valores.cpfNumero,
            categoria: valores.categoria,
            periodo_de: valores.periodoDe,
            periodo_ate: valores.periodoAte,
            nome_pai: valores.nomePai,
            nome_mae: valores.nomeMae,
            data_nascimento: valores.dtNasc,
            nacionalidade: valores.nacionalidade,
            pais_origem_id: valores.pais?.toString(),
            municipio_naturalidade: valores.naturalidadeCidade,
            naturalidade_uf: valores.naturalidadeUf,
            sexo: valores.sexo,
            estado_civil: valores.estadoCivil,
            grupo_sanguineo: valores.tipoSanguineoABO,
            fator_rh: valores.tipoSanguineoRh,
            local_trabalho: valores.localTrabalho,
            visto: valores.visto,
            rg_numero: valores.numeroRg,
            rg_orgao_expedidor: valores.orgExpedidor,
            rg_uf: valores.ufRg,
            rg_data_emissao: valores.dtEmissaoRg,
            te_numero: valores.tituloNumero,
            te_zona: valores.tituloZona,
            te_secao: valores.tituloSecao,
            te_uf: valores.tituloUf,
            reservista_numero: valores.numeroReservista,
            situacao_militar: valores.situacaoMilitar,
            reservista_regiao_militar: valores.regiaoMilitar,
            comando: valores.comandoMilitar,
            numeroCedulaEstrangeira: valores.cedulaEstrangeira,
            dataCedulaEstrangeira: valores.dtEmissaoCedula,
            data_validade_visto: valores.dtValidadeVisto,
            in_estrangeiro: this.booleanParaSimNao(valores.nacionalidade === 'E'),
            er_cep: valores.cep,
            er_municipio_id: valores.municipioId?.toString(),
            er_ddd: valores.telefoneResidencialDdd,
            er_telefone: valores.telefoneResidencialNumero,
            ec_cep: valores.cepCorrespondencia,
            ec_logradouro: valores.enderecoCorrespondenciaTexto,
            ec_bairro: valores.bairroCorrespondencia,
            ec_municipio_id: valores.municipioCorrespondenciaId?.toString(),
            ds_mun_end_c: valores.municipioCorrespondencia,
            ec_ddd: valores.telefoneCorrespondenciaDdd,
            ec_telefone: valores.telefoneCorrespondenciaNumero,
            er_logradouro: valores.endereco,
            er_bairro: valores.bairro,
            ds_mun_end_r: valores.municipio,
            in_endereco_correspondencia: this.booleanParaSimNao(valores.enderecoCorrespondencia),
            divulgar_endereco: this.booleanParaSimNao(valores.divulgarEndereco),
            nm_email: valores.email,
            celular_ddd: valores.telefoneDdd,
            celular_numero: valores.telefoneNumero,
            tipoDeficiencia: valores.deficienciaTipo,
            origemDeficiencia: valores.deficienciaOrigem,
            adaptadoDeficiencia: this.booleanParaSimNao(valores.deficienciaAdaptado),
            comprovante_graduacao: valores.comprovanteGraduacao,
            tipoFormacao: valores.tipoFormacao ? Number(valores.tipoFormacao) : undefined,
            nome_escola: valores.nomeEscola,
            nomeFaculdadeEstrangeira: valores.nomeEscolaEstrangeira,
            registro_mec_livro: valores.livroRegistro,
            registro_mec_folha: valores.folhaNumero,
            registro_mec_numero: valores.registroMec,
            numero_processo: valores.numeroProcesso,
            data_conclusao: valores.dtConclusao,
            data_formatura: valores.dtFormaturaForm,
            data_expedicao_diploma: valores.expDiploma,
            data_registro_mec: valores.dtRegMec
        };
    }

    // Regras condicionais por nacionalidade: 'E' (Estrangeiro) exibe o RNE;
    // demais (Brasileiro/Naturalizado) exibem documentos nacionais.
    get isEstrangeiro(): boolean {
        return this.form?.get('nacionalidade')?.value === 'E';
    }

    private validarFormulario(): string | null {
        const valores = this.form.getRawValue();

        const dtFormaturaTexto = valorTexto(valores.dtFormaturaForm).trim();
        if (!dtFormaturaTexto) {
            return 'A data de formatura e obrigatoria.';
        }

        const dtFormatura = this.parseDataBr(dtFormaturaTexto);
        if (!dtFormatura) {
            return 'Data de formatura invalida (use DD/MM/AAAA).';
        }

        const dtNascimentoTexto = valorTexto(valores.dtNasc).trim();
        if (dtNascimentoTexto) {
            const dtNascimento = this.parseDataBr(dtNascimentoTexto);
            if (dtNascimento && dtFormatura.getTime() < dtNascimento.getTime()) {
                return 'A data de formatura deve ser posterior a data de nascimento.';
            }
        }

        return null;
    }

    private parseDataBr(valor: string): Date | null {
        const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(valor.trim());
        if (!match) {
            return null;
        }

        const dia = Number(match[1]);
        const mes = Number(match[2]);
        const ano = Number(match[3]);
        const data = new Date(ano, mes - 1, dia);

        if (data.getFullYear() !== ano || data.getMonth() !== mes - 1 || data.getDate() !== dia) {
            return null;
        }

        return data;
    }

    private booleanParaSimNao(valor: unknown): string | undefined {
        if (valor === true) {
            return 'S';
        }

        if (valor === false) {
            return 'N';
        }

        return undefined;
    }

    private normalizarBoolean(valor: unknown): boolean | null {
        if (typeof valor === 'boolean') {
            return valor;
        }

        if (typeof valor === 'number') {
            if (valor === 1) {
                return true;
            }

            if (valor === 0) {
                return false;
            }
        }

        const texto = valorTexto(valor).trim().toUpperCase();
        if (['S', 'SIM', 'TRUE', '1'].includes(texto)) {
            return true;
        }

        if (['N', 'NAO', 'FALSE', '0'].includes(texto)) {
            return false;
        }

        return null;
    }

    private atualizarAnexos(response: PreMedicoResponse): void {
        this.anexosDisponiveis.set(
            this.camposAnexos
                .map((campo) => {
                    const valorNormalizado = this.normalizarAnexoValor(response?.[campo.chave]);
                    if (!valorNormalizado) {
                        return null;
                    }

                    return {
                        chave: String(campo.chave),
                        titulo: campo.titulo,
                        linkAnexo: valorNormalizado
                    } satisfies AnexoPreMedico;
                })
                .filter((item): item is AnexoPreMedico => item !== null)
        );
    }

    private normalizarAnexoValor(valor: unknown): string {
        return typeof valor === 'string' ? valor.trim() : '';
    }

    possuiAnexo(anexo: AnexoPreMedico): boolean {
        return !!valorTexto(anexo.linkAnexo).trim();
    }

    onVisualizarAnexo(event: Event, anexo: AnexoPreMedico): void {
        event.preventDefault();
        this.visualizarAnexoPorCampo(anexo.linkAnexo);
    }

    private visualizarAnexoPorCampo(campoAnexo: unknown): void {
        const idAnexo = this.extrairIdAnexo(campoAnexo);
        if (!idAnexo) {
            this.messageService.add({ severity: 'warn', summary: 'Atencao', detail: 'Id do anexo nao identificado.' });
            return;
        }

        this.servico
            .visualizarAnexo(idAnexo)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (blob) => {
                    this.blobAnexo.set(blob.type ? blob : new Blob([blob], { type: 'application/pdf' }));
                    this.exibirDialogAnexo.set(true);
                },
                error: () => {
                    // Mensagem fica a cargo do interceptor central; aqui so o estado local.
                    this.blobAnexo.set(null);
                    this.exibirDialogAnexo.set(false);
                }
            });
    }

    fecharDialogAnexo(): void {
        this.exibirDialogAnexo.set(false);
        this.blobAnexo.set(null);
    }

    private extrairIdAnexo(campoAnexo: unknown): string | null {
        const link = valorTexto(campoAnexo).trim();
        if (!link) {
            return null;
        }

        const regexPorCaminho = /\/(?:webservices\/)?baixar-anexo\/([^/?#]+)/i;
        const matchDireto = regexPorCaminho.exec(link);
        if (matchDireto?.[1]) {
            return decodeURIComponent(matchDireto[1]);
        }

        try {
            const url = new URL(link, window.location.origin);
            const matchPathname = regexPorCaminho.exec(url.pathname);
            if (matchPathname?.[1]) {
                return decodeURIComponent(matchPathname[1]);
            }

            const idQuery = url.searchParams.get('idAnexo') || url.searchParams.get('id');
            if (idQuery?.trim()) {
                return idQuery.trim();
            }
        } catch {
            // Ignora parsing de URL e continua fallback textual.
        }

        if (/^[A-Za-z0-9_-]+$/.test(link)) {
            return link;
        }

        return null;
    }
}
